import React, { useRef, useEffect, useState } from "react";

const API = "https://canada-billing.ameontech.com";

const PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];
const CURRENCIES = ["CAD", "USD", "EUR", "GBP"];
const DATE_FORMATS = [
  { value: "YYYY-MM-DD",   label: "YYYY-MM-DD  (2025-06-06)" },
  { value: "DD/MM/YYYY",   label: "DD/MM/YYYY  (06/06/2025)" },
  { value: "MM/DD/YYYY",   label: "MM/DD/YYYY  (06/06/2025)" },
  { value: "MMM DD, YYYY", label: "MMM DD, YYYY  (Jun 06, 2025)" },
];

const Field = ({ label, sub, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <label style={{ fontSize: 13, fontWeight: 600, color: "#2d3748" }}>{label}</label>
    {sub && <span style={{ fontSize: 12, color: "#a0aec0" }}>{sub}</span>}
    {children}
  </div>
);

const Row2 = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
    {children}
  </div>
);

const SectionTitle = ({ icon, title, sub }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "28px 0 16px" }}>
    <div style={{
      width: 32, height: 32, borderRadius: 8, background: "rgba(79,142,247,0.1)",
      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16
    }}>{icon}</div>
    <div>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#2d3748" }}>{title}</div>
      {sub && <div style={{ fontSize: 12, color: "#a0aec0" }}>{sub}</div>}
    </div>
  </div>
);

const inp = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: "1.5px solid #e2e8f0", fontSize: 14, color: "#2d3748",
  outline: "none", boxSizing: "border-box", background: "#fafbfc",
  transition: "border 0.2s",
};

export default function ProfileBusiness({ profile, setProfile }) {
  const logoInputRef      = useRef(null);
  const signatureInputRef = useRef(null);
  const [loading, setLoading] = useState(true);

  const token   = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const [profileRes, userRes] = await Promise.all([
          fetch(`${API}/api/business-profile`, { headers }),
          fetch(`${API}/api/user/me`,           { headers }),
        ]);
        const profileData = await profileRes.json();
        const userData    = await userRes.json();

        // FIX: handle both { profile: {...} } and {...} directly from backend
        const p = profileData.profile || profileData || {};
        const u = userData.user       || userData    || {};

        setProfile({
          name:               u.full_name            || "",
          email:              u.email                || p.email || "",
          phone:              p.phone                || "",
          website:            p.website              || "",
          company:            p.business_name        || "",
          businessType:       p.business_type        || "",
          businessNumber:     p.business_number      || "",
          hstNumber:          p.hst_number           || "",
          naicsCode:          p.naics_code           || "",
          addressLine1:       p.address_line1        || "",
          addressLine2:       p.address_line2        || "",
          city:               p.city                 || "",
          province:           p.province             || "ON",
          postalCode:         p.postal_code          || "",
          country:            p.country              || "Canada",
          currency:           p.currency             || "CAD",
          dateFormat:         p.date_format          || "MM/DD/YYYY",
          invoicePrefix:      p.invoice_prefix       || "INV",
          invoiceStartNumber: p.invoice_start_number || 1,
          dueDays:            p.due_days             || 30,
          footerNote:         p.footer_note          || "",
          terms:              p.terms                || "",
          logoUrl:      p.logo_url      ? `${API}${p.logo_url}`      : "",
          signatureUrl: p.signature_url ? `${API}${p.signature_url}` : "",
        });
      } catch (err) {
        console.error("Failed to load profile:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("logo", file);
    try {
      const res  = await fetch(`${API}/api/business-profile/logo`, { method: "POST", headers, body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setProfile(p => ({ ...p, logoUrl: `${API}${data.logo_url}` }));
    } catch (err) { console.error("Logo upload failed:", err); }
  };

  const handleLogoRemove = () => {
    setProfile(p => ({ ...p, logoUrl: "" }));
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("signature", file);
    try {
      const res  = await fetch(`${API}/api/business-profile/signature`, { method: "POST", headers, body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setProfile(p => ({ ...p, signatureUrl: `${API}${data.signature_url}` }));
    } catch (err) { console.error("Signature upload failed:", err); }
  };

  const handleSignatureRemove = () => {
    setProfile(p => ({ ...p, signatureUrl: "" }));
    if (signatureInputRef.current) signatureInputRef.current.value = "";
  };

  const set = (key) => (e) => setProfile(p => ({ ...p, [key]: e.target.value }));

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "#718096" }}>Loading profile...</div>
  );

  return (
    <>
      <div className="st-card-hdr">
        <div className="st-card-hdr-icon" style={{ background: "rgba(79,142,247,.1)" }}>👤</div>
        <div>
          <div className="st-card-hdr-title">Profile & Business</div>
          <div className="st-card-hdr-sub">Your personal and business information</div>
        </div>
      </div>

      <div className="st-card-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>

        <SectionTitle icon="👤" title="Personal Information" sub="Your contact details" />
        <Row2>
          <Field label="Full Name">
            <input style={inp} value={profile.name || ""} onChange={set("name")} placeholder="John Doe" />
          </Field>
          <Field label="Email Address">
            <input style={inp} type="email" value={profile.email || ""} onChange={set("email")} placeholder="john@example.com" />
          </Field>
        </Row2>
        <Row2>
          <Field label="Phone Number">
            <input style={inp} value={profile.phone || ""} onChange={set("phone")} placeholder="416-555-0123" />
          </Field>
          <Field label="Website">
            <input style={inp} value={profile.website || ""} onChange={set("website")} placeholder="https://yoursite.ca" />
          </Field>
        </Row2>

        <div style={{ borderTop: "1.5px solid #f0f0f0", margin: "4px 0" }} />

        <SectionTitle icon="🏢" title="Business Information" sub="Your company and tax details" />
        <Row2>
          <Field label="Business / Company Name">
            <input style={inp} value={profile.company || ""} onChange={set("company")} placeholder="Smith Consulting Inc." />
          </Field>
          <Field label="Business Type" sub="e.g. Sole Proprietor, Corporation">
            <input style={inp} value={profile.businessType || ""} onChange={set("businessType")} placeholder="Corporation" />
          </Field>
        </Row2>
        <Row2>
          <Field label="Business Number (BN)" sub="CRA 9-digit business number">
            <input style={inp} value={profile.businessNumber || ""} onChange={set("businessNumber")} placeholder="123456789" />
          </Field>
          <Field label="GST / HST Number" sub="e.g. 123456789 RT0001">
            <input style={inp} value={profile.hstNumber || ""} onChange={set("hstNumber")} placeholder="123456789 RT0001" />
          </Field>
        </Row2>
        <Row2>
          <Field label="NAICS Code" sub="6-digit industry classification">
            <input style={inp} value={profile.naicsCode || ""} onChange={set("naicsCode")} placeholder="541511" />
          </Field>
          <div />
        </Row2>

        <div style={{ borderTop: "1.5px solid #f0f0f0", margin: "4px 0" }} />

        <SectionTitle icon="🖼️" title="Logo & Signature" sub="Shown on invoices and documents" />
        <Row2>
          <Field label="Business Logo" sub="PNG, JPG or SVG. Max 2MB.">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {profile.logoUrl && (
                <img src={profile.logoUrl} alt="Logo"
                  style={{ width: 110, height: 55, objectFit: "contain", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: 4, background: "#fff" }} />
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button className="st-avatar-btn" onClick={() => logoInputRef.current?.click()}>
                  {profile.logoUrl ? "Change" : "Upload Logo"}
                </button>
                {profile.logoUrl && (
                  <button className="st-avatar-btn" style={{ color: "#e53e3e", borderColor: "#e53e3e" }} onClick={handleLogoRemove}>Remove</button>
                )}
              </div>
              <input ref={logoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleLogoUpload} />
            </div>
          </Field>

          <Field label="Signature" sub="PNG or JPG with transparent background recommended.">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {profile.signatureUrl && (
                <img src={profile.signatureUrl} alt="Signature"
                  style={{ width: 160, height: 55, objectFit: "contain", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: 4, background: "#fff" }} />
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button className="st-avatar-btn" onClick={() => signatureInputRef.current?.click()}>
                  {profile.signatureUrl ? "Change" : "Upload Signature"}
                </button>
                {profile.signatureUrl && (
                  <button className="st-avatar-btn" style={{ color: "#e53e3e", borderColor: "#e53e3e" }} onClick={handleSignatureRemove}>Remove</button>
                )}
              </div>
              <input ref={signatureInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleSignatureUpload} />
            </div>
          </Field>
        </Row2>

        <div style={{ borderTop: "1.5px solid #f0f0f0", margin: "4px 0" }} />

        <SectionTitle icon="📍" title="Address" sub="Your business location" />
        <Field label="Address Line 1">
          <input style={inp} value={profile.addressLine1 || ""} onChange={set("addressLine1")} placeholder="123 Main St" />
        </Field>
        <Field label="Address Line 2">
          <input style={inp} value={profile.addressLine2 || ""} onChange={set("addressLine2")} placeholder="Suite 100" />
        </Field>
        <Row2>
          <Field label="City">
            <input style={inp} value={profile.city || ""} onChange={set("city")} placeholder="Toronto" />
          </Field>
          <Field label="Province">
            <select style={inp} value={profile.province || "ON"} onChange={set("province")}>
              {PROVINCES.map(pr => <option key={pr} value={pr}>{pr}</option>)}
            </select>
          </Field>
        </Row2>
        <Row2>
          <Field label="Postal Code">
            <input style={inp} value={profile.postalCode || ""} onChange={set("postalCode")} placeholder="M5V 1A1" />
          </Field>
          <Field label="Country">
            <input style={inp} value={profile.country || ""} onChange={set("country")} placeholder="Canada" />
          </Field>
        </Row2>

        <div style={{ borderTop: "1.5px solid #f0f0f0", margin: "4px 0" }} />

        <SectionTitle icon="🧾" title="Invoice Settings" sub="Currency, format, and invoice defaults" />
        <Row2>
          <Field label="Currency" sub="Used on all invoices and reports">
            <select style={inp} value={profile.currency || "CAD"} onChange={set("currency")}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Date Format" sub="How dates appear on invoices">
            <select style={inp} value={profile.dateFormat || "MM/DD/YYYY"} onChange={set("dateFormat")}>
              {DATE_FORMATS.map(df => <option key={df.value} value={df.value}>{df.label}</option>)}
            </select>
          </Field>
        </Row2>
        <Row2>
          <Field label="Invoice Prefix" sub="e.g. INV, BILL, CAD">
            <input style={inp} value={profile.invoicePrefix || ""} onChange={set("invoicePrefix")} placeholder="INV" />
          </Field>
          <Field label="Invoice Start Number">
            <input style={inp} type="number" value={profile.invoiceStartNumber || ""} onChange={set("invoiceStartNumber")} placeholder="1" />
          </Field>
        </Row2>
        <Row2>
          <Field label="Default Due Days" sub="Days until invoice is due">
            <input style={inp} type="number" value={profile.dueDays || ""} onChange={set("dueDays")} placeholder="30" />
          </Field>
          <div />
        </Row2>
        <Field label="Footer Note">
          <textarea style={{ ...inp, resize: "vertical" }} rows={2}
            value={profile.footerNote || ""} onChange={set("footerNote")}
            placeholder="Thank you for your business!" />
        </Field>
        <Field label="Terms & Conditions">
          <textarea style={{ ...inp, resize: "vertical" }} rows={3}
            value={profile.terms || ""} onChange={set("terms")}
            placeholder="Payment due within 30 days..." />
        </Field>

      </div>
    </>
  );
}